import { useState } from 'react';
import {
  useCreatePromotion,
  useUpdatePromotion,
  dollarsToCents,
} from '../../../../domain/Promotions';
import { MAX_PROMOTION_VALUE } from '../constants';
import { parseErrorMessage } from '../utils';

// $XX.XX — numbers with an optional 1-2 decimal part.
const MAX_VALUE_PATTERN = /^\d+(\.\d{1,2})?$/;

// Owns all create/edit promotion form logic (state, validation, mutations)
// so PromotionFormModal stays presentational.
export const usePromotionForm = ({ mode, promotion, onClose }) => {
  const isEdit = mode === 'edit';
  const [name, setName] = useState(promotion?.name ?? '');
  const [maxValue, setMaxValue] = useState(
    isEdit ? promotion?.maxValueDisplay ?? '' : '',
  );
  const [errorMessage, setErrorMessage] = useState('');

  const handleError = (error) => setErrorMessage(parseErrorMessage(error));

  const createMutation = useCreatePromotion({ onSuccess: onClose, onError: handleError });
  const updateMutation = useUpdatePromotion({ onSuccess: onClose, onError: handleError });

  const trimmedName = name.trim();
  const normalizedMax = String(maxValue).replace(/[^0-9.]/g, '');
  const isValid =
    trimmedName.length >= 1 &&
    MAX_VALUE_PATTERN.test(normalizedMax) &&
    Number(normalizedMax) > 0 &&
    Number(normalizedMax) <= MAX_PROMOTION_VALUE;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    if (!isValid || isSaving) return;
    setErrorMessage('');
    const maxPayoutAmountCents = dollarsToCents(normalizedMax);

    if (isEdit) {
      const changes = {};
      if (trimmedName !== promotion.name) changes.name = trimmedName;
      if (maxPayoutAmountCents !== promotion.maxValueCents) {
        changes.maxPayoutAmountCents = maxPayoutAmountCents;
      }
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      updateMutation.mutate({ promotionId: promotion.id, changes });
    } else {
      createMutation.mutate({ name: trimmedName, maxPayoutAmountCents });
    }
  };

  return {
    isEdit,
    name,
    setName,
    maxValue,
    setMaxValue,
    errorMessage,
    isValid,
    isSaving,
    handleSubmit,
  };
};
